import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fetchMakers, submitApplication } from "@/lib/api";
import { PRODUCT_TYPES } from "@/lib/constants";
import { resolveImageUrl } from "@/lib/whatsapp";

const INITIAL = {
  full_name: "",
  business_name: "",
  phone: "",
  product_type: "",
  city: "",
};

export default function Makers() {
  const location = useLocation();
  const [makers, setMakers] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [makersLoading, setMakersLoading] = useState(true);

  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    document.title = "Makers — OVI";
    let mounted = true;
    (async () => {
      try {
        const list = await fetchMakers();
        if (!mounted) return;
        setMakers(list);
        setFeatured(list.find((m) => m.featured) || list[0] || null);
      } catch (e) {
        console.error(e);
      } finally {
        if (mounted) setMakersLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Smooth-scroll to the application form when arriving with #maker-application hash
  useEffect(() => {
    if (location.hash === "#maker-application") {
      // Wait for content to be painted
      const t = setTimeout(() => {
        document.getElementById("maker-application")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 220);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: typeof e === "string" ? e : e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
    if (!form.business_name.trim() || form.business_name.trim().length < 2) e.business_name = "Please enter your business name.";
    const cleanedPhone = form.phone.replace(/[\s\-()]/g, "");
    if (!/^\+?\d{7,15}$/.test(cleanedPhone)) e.phone = "Enter a valid phone number.";
    if (!form.product_type) e.product_type = "Please choose your product type.";
    if (!form.city.trim() || form.city.trim().length < 2) e.city = "Please enter your city.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitApplication(form);
      setSuccess(true);
      setForm(INITIAL);
      toast.success("Application received. We'll reach out within 2 working days.");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't submit. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[var(--ovi-cream)]">
      {/* FEATURED MAKER */}
      {featured ? (
        <section data-testid="makers-featured-section">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 lg:pt-14 pb-0">
            <p className="text-xs uppercase tracking-[0.24em] text-[var(--ovi-terracotta)] font-semibold mb-4">
              Featured maker
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-10 items-center">
              <div className="lg:col-span-5">
                <div className="relative rounded-2xl overflow-hidden border border-[var(--ovi-border)] aspect-[4/5] lg:aspect-[5/6]">
                  <img
                    src={resolveImageUrl(featured.photo_url)}
                    alt={`${featured.full_name}, ${featured.specialty} — Solapur`}
                    className="absolute inset-0 h-full w-full object-cover"
                    loading="eager"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[rgba(20,22,27,0.55)] to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-[10px] sm:text-xs uppercase tracking-[0.22em] text-[var(--ovi-cream-2)] font-semibold">
                    · {featured.city} ·
                  </div>
                </div>
              </div>
              <div className="lg:col-span-7">
                <p className="text-xs uppercase tracking-[0.18em] text-[var(--ovi-muted-ink)] font-semibold">
                  {featured.specialty}
                  {featured.years_of_craft > 0 ? ` · ${featured.years_of_craft}+ years at the loom` : ""}
                </p>
                <h2
                  className="mt-2 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[var(--ovi-indigo)] leading-tight"
                  data-testid="makers-featured-name"
                >
                  {featured.full_name}
                </h2>
                <blockquote
                  className="mt-4 font-serif-display italic text-lg sm:text-xl lg:text-2xl text-[var(--ovi-ink)] leading-snug border-l-2 border-[var(--ovi-terracotta)] pl-5"
                  data-testid="makers-featured-quote"
                >
                  “{featured.quote}”
                </blockquote>
                <p className="mt-4 text-sm sm:text-base text-[var(--ovi-muted-ink)] leading-relaxed">
                  {featured.story_paragraphs?.[0]}
                </p>
                <div className="mt-5">
                  <Button
                    asChild
                    className="bg-[var(--ovi-terracotta)] hover:bg-[var(--ovi-terracotta-dark)] text-[var(--ovi-cream-2)] rounded-full h-11 px-6 active:scale-[0.98] transition-[background-color,transform] duration-200"
                  >
                    <Link
                      to={`/makers/${featured.slug}`}
                      data-testid="makers-featured-profile-link"
                    >
                      Read {featured.first_name}'s full profile
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* OVI Onboarding banner \u2014 immediately follows featured (no gap) */}
      <section className="mt-6 sm:mt-8 bg-[var(--ovi-terracotta)] text-[var(--ovi-cream-2)] ovi-noise" data-testid="makers-onboarding-banner">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm sm:text-base leading-snug">
            <span className="font-medium">OVI is currently onboarding India's first verified makers.</span>{" "}
            <span className="text-[var(--ovi-cream-2)]/90">Are you a maker? Apply below.</span>
          </p>
          <a
            href="#maker-application"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("maker-application")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold uppercase tracking-[0.16em] bg-[var(--ovi-cream-2)] text-[var(--ovi-terracotta)] hover:bg-[var(--ovi-cream)] rounded-full px-5 py-2 transition-colors w-fit"
            data-testid="makers-onboarding-banner-cta"
          >
            Apply to join
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* HERITAGE STORY + ALL MAKERS GRID */}
      <section className="ovi-texture">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.24em] text-[var(--ovi-terracotta)] font-semibold mb-4">
                For makers of India
              </p>
              <h1
                className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[var(--ovi-indigo)] tracking-tight leading-[1.05]"
                data-testid="makers-page-title"
              >
                From every loom in India. One platform.
              </h1>
              <p className="mt-6 text-base sm:text-lg text-[var(--ovi-muted-ink)] leading-relaxed max-w-2xl">
                Solapur is one of India's oldest textile clusters — home to thousands of weavers,
                power-looms, and family-run units that have been making bed sheets, chaddars,
                terry towels and denim for over a hundred years.
              </p>
              <p className="mt-4 text-base sm:text-lg text-[var(--ovi-muted-ink)] leading-relaxed max-w-2xl">
                But too many makers still sell at thin margins through long chains of middlemen.
                <span className="text-[var(--ovi-indigo)] font-medium"> OVI exists to change that.</span>
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-[var(--ovi-border)]">
                <img
                  src="https://images.unsplash.com/photo-1653580373915-4ddcfa0699eb?auto=format&fit=crop&w=1200&q=80"
                  alt="Indigo dyed handloom textile"
                  className="w-full h-[320px] sm:h-[400px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All makers grid */}
      <section className="bg-[var(--ovi-cream-2)] border-y border-[var(--ovi-border)]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="flex items-end justify-between gap-4 mb-8 sm:mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--ovi-terracotta)] font-semibold mb-3">
                The OVI makers
              </p>
              <h2 className="font-serif-display text-3xl sm:text-4xl text-[var(--ovi-indigo)]">
                Names behind the cloth.
              </h2>
            </div>
          </div>

          {makersLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-[4/5] rounded-2xl bg-[var(--ovi-border)]/40 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="makers-grid">
              {makers.map((m, i) => (
                <Link
                  key={m.id}
                  to={`/makers/${m.slug}`}
                  data-testid={`maker-card-${m.slug}`}
                  className="group relative block rounded-2xl overflow-hidden border border-[var(--ovi-border)] bg-[var(--ovi-cream)] hover:border-[rgba(30,42,71,0.22)] transition-colors"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-30px" }}
                    transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.25), ease: [0.2, 0.7, 0.2, 1] }}
                    className="aspect-[4/5] relative"
                  >
                    <img
                      src={resolveImageUrl(m.photo_url)}
                      alt={`${m.full_name}, ${m.specialty}`}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[600ms] group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[rgba(20,22,27,0.65)] via-[rgba(20,22,27,0.25)] to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3 text-[var(--ovi-cream-2)]">
                      <p className="font-serif-display text-lg sm:text-xl leading-tight">{m.first_name}</p>
                      <p className="text-[10px] sm:text-xs uppercase tracking-[0.16em] mt-0.5 opacity-90">{m.specialty}</p>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Application Form */}
      <section id="maker-application" className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20 scroll-mt-20">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.24em] text-[var(--ovi-terracotta)] font-semibold mb-4">
            Apply to join
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[var(--ovi-indigo)]">
            Become an OVI maker.
          </h2>
          <p className="mt-3 text-sm sm:text-base text-[var(--ovi-muted-ink)]">
            We'll reach out within 2 working days.
          </p>
        </div>

        {success ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[var(--ovi-cream-2)] border border-[var(--ovi-border)] rounded-2xl p-8 sm:p-10 text-center"
            data-testid="maker-form-success-message"
          >
            <CheckCircle2 className="h-10 w-10 text-[var(--ovi-terracotta)] mx-auto" />
            <h3 className="mt-4 font-serif-display text-2xl text-[var(--ovi-indigo)]">
              Thank you for applying.
            </h3>
            <p className="mt-2 text-sm sm:text-base text-[var(--ovi-muted-ink)] max-w-md mx-auto">
              Your application has reached us. Our team will call you within 2 working days.
            </p>
            <Button
              onClick={() => setSuccess(false)}
              variant="outline"
              className="mt-6 border-[var(--ovi-indigo)] text-[var(--ovi-indigo)] rounded-full h-11 px-6"
              data-testid="maker-form-submit-another"
            >
              Submit another application
            </Button>
          </motion.div>
        ) : (
          <form
            onSubmit={onSubmit}
            noValidate
            data-testid="maker-application-form"
            className="bg-[var(--ovi-cream-2)] border border-[var(--ovi-border)] rounded-2xl p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-5"
          >
            <Field id="full_name" label="Full name" error={errors.full_name} span="md:col-span-2">
              <Input id="full_name" value={form.full_name} onChange={update("full_name")}
                placeholder="Asha Patil" className="app-input"
                data-testid="maker-form-full-name-input" autoComplete="name" />
            </Field>
            <Field id="business_name" label="Business name" error={errors.business_name} span="md:col-span-2">
              <Input id="business_name" value={form.business_name} onChange={update("business_name")}
                placeholder="Patil Handlooms" className="app-input"
                data-testid="maker-form-business-name-input" autoComplete="organization" />
            </Field>
            <Field id="phone" label="Phone" error={errors.phone}>
              <Input id="phone" value={form.phone} onChange={update("phone")}
                placeholder="+91 00000 00000" inputMode="tel" className="app-input"
                data-testid="maker-form-phone-input" autoComplete="tel" />
            </Field>
            <Field id="product_type" label="Product type" error={errors.product_type}>
              <Select value={form.product_type} onValueChange={update("product_type")}>
                <SelectTrigger id="product_type" className="app-input h-11" data-testid="maker-form-product-type-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--ovi-cream-2)] border-[var(--ovi-border)]">
                  {PRODUCT_TYPES.map((t) => (
                    <SelectItem key={t} value={t} className="focus:bg-[var(--ovi-cream)]"
                      data-testid={`maker-form-product-type-option-${t.replace(/\s+/g, "-").toLowerCase()}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field id="city" label="City" error={errors.city} span="md:col-span-2">
              <Input id="city" value={form.city} onChange={update("city")}
                placeholder="Solapur" className="app-input"
                data-testid="maker-form-city-input" autoComplete="address-level2" />
            </Field>
            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
              <p className="text-xs text-[var(--ovi-muted-ink)] max-w-sm">
                By submitting, you agree to be contacted by OVI for onboarding.
              </p>
              <Button type="submit" disabled={submitting}
                className="bg-[var(--ovi-terracotta)] hover:bg-[var(--ovi-terracotta-dark)] text-[var(--ovi-cream-2)] rounded-full h-12 px-7 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-70"
                data-testid="maker-form-submit-button">
                {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting…</>) : "Apply to join"}
              </Button>
            </div>
          </form>
        )}
      </section>

      <style>{`
        .app-input { background-color: var(--ovi-cream); border-color: var(--ovi-border); }
        .app-input:focus-visible { --tw-ring-color: var(--ovi-terracotta); }
      `}</style>
    </div>
  );
}

const Field = ({ id, label, error, children, span = "" }) => (
  <div className={`flex flex-col gap-1.5 ${span}`}>
    <Label htmlFor={id} className="text-xs uppercase tracking-[0.18em] font-medium text-[var(--ovi-muted-ink)]">
      {label}
    </Label>
    {children}
    {error ? (
      <p className="text-xs text-[var(--ovi-terracotta)] mt-0.5" data-testid={`maker-form-${id}-error`}>
        {error}
      </p>
    ) : null}
  </div>
);
